import React, { useContext } from 'react';
import { CoursePricingOption } from '../../../../../../../store/features/coursePricing';
import { getDeliveryTypeName } from '../../../../../../../helpers/getDeliveryTypeName';
import { getOptionDurationValue, getOptionTimeValue } from '../../../helpers';
import { CourseOptionDisplayModuleContext } from '../../../context';
import {
  BoldParagraph,
  DiscountParagraph,
  FlexStart,
  FlexWrapped,
  Link,
  Paragraph,
  PriceParagraph,
  SalesMessage,
  StyledBookButton,
  StyledOptionDisplayCardSection,
  StyledDeliveryType,
  StyledDownloadBrochureLink,
  TierParagraph,
} from '../../../styledComponents';
import {
  BookButton,
  DownloadBrochure,
  Language,
  Location,
  StartDate,
  Tier,
  Trainer,
} from '../../../dataComponents';

export type OptionDisplayCardProps = {
  option: CoursePricingOption;
};

export const OptionDisplayCard = ({ option }: OptionDisplayCardProps) => {
  const {
    section: {
      showSettings: {
        showDate,
        showTime,
        showDuration,
        showLocation,
        showTimezone,
        showLanguage,
        showDownloadBrochureLink,
        showVenue,
        showSalesMessage,
        showTrainers,
      },
    },
  } = useContext(CourseOptionDisplayModuleContext);

  const courseOptionDuration = getOptionDurationValue(
    showDuration,
    option.amountOfDays,
    option.numberOfDaysOverride,
  );

  const courseOptionTimeText = getOptionTimeValue({
    showTime,
    showTimezone,
    time: option.time,
    timeZone: option.timeZone,
    timeZoneAsText: option.timeZoneAsText,
  });

  const showDateInfo =
    showDate &&
    (option.startDate || option.dateAsText || option.numberOfDaysOverride);

  return (
    <>
      {showDateInfo && (
        <StyledOptionDisplayCardSection>
          {option.startDate ? (
            <StartDate value={option.startDate} as={BoldParagraph} />
          ) : (
            <BoldParagraph text={option.dateAsText} />
          )}

          {(courseOptionDuration || courseOptionTimeText) && (
            <Paragraph
              text={[courseOptionDuration, courseOptionTimeText]
                .filter(Boolean)
                .join(', ')}
            />
          )}
        </StyledOptionDisplayCardSection>
      )}
      <StyledOptionDisplayCardSection>
        <StyledDeliveryType text={getDeliveryTypeName(option.deliveryType)} />
        {option.location && (showLocation || showVenue) && (
          <Location
            showLocation={showLocation}
            showVenue={showVenue}
            location={option.location}
            as={Paragraph}
          />
        )}
      </StyledOptionDisplayCardSection>
      {(showLanguage || showDownloadBrochureLink) && (
        <StyledOptionDisplayCardSection>
          {showLanguage && option.language && (
            <Language value={option.language} prefix as={Paragraph} />
          )}
          {showDownloadBrochureLink && (
            <DownloadBrochure
              ctaUrl={option.brochureCtaUrl}
              ctaText={option.brochureCtaText}
              newTab={option.openBrochureInNewTab}
              as={StyledDownloadBrochureLink}
            />
          )}
        </StyledOptionDisplayCardSection>
      )}
      {showTrainers && option.trainers?.length > 0 && (
        <StyledOptionDisplayCardSection>
          {option.trainers.map((trainer, trainerIndex) => {
            return (
              <Trainer
                key={trainerIndex}
                uri={trainer.uri}
                displayName={trainer.displayName}
                as={Link}
                prefix
              />
            );
          })}
        </StyledOptionDisplayCardSection>
      )}
      <FlexWrapped>
        <div>
          <FlexStart>
            {option.primaryProductDiscountedPrice ? (
              <>
                <PriceParagraph text={option.primaryProductDiscountedPrice} />
                <DiscountParagraph text={option.primaryProductFullPrice} />
              </>
            ) : (
              <PriceParagraph text={option.primaryProductFullPrice} />
            )}
          </FlexStart>
          {option.tier?.endDate && option.tier?.maxSaving && (
            <Tier
              endDate={option.tier.endDate}
              maxSaving={option.tier.maxSaving}
              as={TierParagraph}
            />
          )}
        </div>
        <div className="text-right">
          <BookButton
            bookingOpen={option.bookingOpen}
            bookingClosedText={option.bookingClosedText}
            bookingLinkOverride={option.bookingLinkOverride}
            bookingButtonTextOverride={option.bookingButtonTextOverride}
            optionIndex={option.index}
            as={StyledBookButton}
          />
          {showSalesMessage && option.salesMessage && (
            <SalesMessage text={option.salesMessage} />
          )}
        </div>
      </FlexWrapped>
    </>
  );
};
